import React from 'react';
import Joi from 'joi';
import _ from 'lodash';

import { array, object as PropTypesObject, func } from 'prop-types';
import { SelectOption } from '../../../utils/models';
import { matchPhrases } from '../../../utils/utilities';
import { OTHER_DESCRIPTION } from '../../../utils/constants';

import Button from '../../themed/button';
import Conditional from '../../common/conditional';
import Form from '../../common/form';

import toastService from '../../../services/toastService';

class CustomUTMInspectionItemEditForm extends Form {
	state = {
		data: {
			tmlLocation: '',
			description: '',
			otherDescription: '',
			nominal: '',
			comments: '',
		},
		errors: {},
		loading: false,
		progress: 0,
		columns: [],
	};

	schema: any = {
		tmlLocation: Joi.string().required().label('TML Location'),
		description: Joi.string().min(1).required().label('Description'),
		otherDescription: Joi.string().when('description', {
			is: OTHER_DESCRIPTION,
			then: Joi.string().min(3).required().label('Other Description'),
			otherwise: Joi.string().allow('').label('Other Description'),
		}),
		nominal: Joi.alternatives().try(Joi.number().min(0), Joi.string().valid('NG')).required().label('Nominal'),
		comments: Joi.string().min(3).required().label('Comments'),
	};

	static propTypes = {
		inspectedItem: PropTypesObject,
		options: array,
		onEdit: func,
		onCancelEdit: func,
	};

	componentDidMount() {
		this.mapInitialData();
	}

	mapInitialData = () => {
		const initialData = { ...this.props.inspectedItem };
		const optionMatch = this.props.options.find((option: SelectOption) => matchPhrases(option.id.toString(), this.props.inspectedItem.description));

		if (optionMatch) {
			initialData.description = optionMatch.id;
			initialData.otherDescription = '';
		} else {
			initialData.otherDescription = initialData.description;
			initialData.description = OTHER_DESCRIPTION;
		}

		if (initialData.size) {
			this.schema.size = Joi.number().min(1).required().label('Size (Inches)');
		}

		const columns = _.omit(initialData, ['tmlLocation', 'description', 'otherDescription', 'nominal', 'comments']);
		const columnsList = Object.keys(columns);
		for (const key of columnsList) {
			this.schema[key] = Joi.alternatives().try(Joi.number().min(0), Joi.string().valid('N/A')).required().label(key);
		}

		this.setState({ data: initialData, columns: columnsList } as any);
	};

	getDescriptionLabel = () => {
		if (this.state.data.description === OTHER_DESCRIPTION) {
			return this.state.data.otherDescription;
		}

		return this.state.data.description;
	};

	doSubmit = (): void => {
		const description = this.getDescriptionLabel();
		if (matchPhrases(description, OTHER_DESCRIPTION)) return toastService.error('Description cannot be others');

		const { otherDescription, ...data } = this.state.data;
		this.props.onEdit({ ...data, description });
	};

	render() {
		return (
			<form autoComplete="off" onSubmit={this.handleSubmit}>
				<div className="row">
					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'tmlLocation',
							label: 'TML Location',
							placeholder: 'Enter TML Location',
						})}
					</div>

					<div className="col">
						{this.renderSelect({
							options: this.props.options,
							name: 'description',
							placeholder: 'Select Description',
							label: 'Description',
						})}
					</div>

					<Conditional isVisible={this.state.data.description === OTHER_DESCRIPTION}>
						<div className="col">
							{this.renderInput({
								name: 'otherDescription',
								type: 'text',
								placeholder: 'Enter Other Description',
								label: 'Other Description',
							})}
						</div>
					</Conditional>

					<Conditional isVisible={(this.state.data as any).size !== undefined}>
						<div className="col">
							{this.renderInput({
								type: 'number',
								name: 'size',
								label: 'Size (Inches)',
								placeholder: 'e.g. Inches',
							})}
						</div>
					</Conditional>

					<div className="col">
						{this.renderInput({
							type: 'text',
							name: 'nominal',
							label: 'Nominal (mm)',
							placeholder: 'Enter Nominal',
						})}
					</div>
				</div>

				<div className="divider my-4"></div>

				<div className="row">
					{this.state.columns.map((column: string, index: number) => (
						<React.Fragment key={index}>
							<div className="col">
								{this.renderInput({
									type: 'text',
									name: column,
									label: column,
									placeholder: `Enter '${column}' value`,
								})}
							</div>

							<Conditional isVisible={(index + 1) % 3 === 0}>
								<div className="w-100"></div>
							</Conditional>
						</React.Fragment>
					))}
				</div>

				<div className="divider my-4"></div>

				{this.renderTextArea({
					rows: '4',
					name: 'comments',
					label: 'Comments.',
					placeholder: 'Enter your comments here',
				})}

				<div className="d-flex justify-content-end">
					<div className="me-4">
						{this.renderButton('Save Changes')}
					</div>

					<Button 
						label='Cancel' 
						style={{ backgroundColor: 'red' }}
						onClick={this.props.onCancelEdit} 
					/>
				</div>
			</form>
		);
	}
}

export default CustomUTMInspectionItemEditForm;
